import { Domain, Difficulty, Track, type Domain as DomainType, type Question } from "../../domain/types";
import { AppError, ErrorClassification } from "../../domain/errors";
import { DISPLAY_CHOICE_IDS } from "../../domain/choice-order";
import { traceAsync } from "../../infrastructure/trace";

type QuestionModule = { readonly questions: unknown };
type QuestionLoader = () => Promise<QuestionModule>;

const QUESTION_CHUNK_LOADERS: Readonly<Record<DomainType, QuestionLoader>> = {
  [Domain.APP_FOUNDATIONS]: () => import("./application/foundations"),
  [Domain.ACCOUNT_DEFAULT_SECURITY]: () => import("./application/account-default-security"),
  [Domain.DNS_TLS]: () => import("./application/dns-tls"),
  [Domain.WAF_MANAGED_RULES]: () => import("./application/waf-managed-rules"),
  [Domain.CUSTOM_RULES_LISTS]: () => import("./application/custom-rules-lists"),
  [Domain.RATE_LIMITING_DDOS]: () => import("./application/rate-limiting-ddos"),
  [Domain.API_BOT_SECURITY]: () => import("./application/api-bot-security"),
  [Domain.ANALYTICS_TROUBLESHOOTING]: () => import("./application/analytics-troubleshooting"),
  [Domain.ZERO_TRUST_FOUNDATIONS]: () => import("./zero-trust/foundations"),
  [Domain.IDENTITY_ENROLLMENT]: () => import("./zero-trust/identity-enrollment"),
  [Domain.CLIENT_DEVICE_POSTURE]: () => import("./zero-trust/client-device-posture"),
  [Domain.TUNNELS_MESH_ROUTING]: () => import("./zero-trust/tunnels-mesh-routing"),
  [Domain.ACCESS_POLICIES]: () => import("./zero-trust/access-policies"),
  [Domain.GATEWAY_POLICIES]: () => import("./zero-trust/gateway-policies"),
  [Domain.TLS_DLP_CASB_ISOLATION]: () => import("./zero-trust/tls-dlp-casb-isolation"),
  [Domain.ZERO_TRUST_OPERATIONS]: () => import("./zero-trust/operations"),
};

export async function loadQuestionChunks(
  traceId: string,
  domains: readonly DomainType[],
): Promise<readonly Question[]> {
  const uniqueDomains = [...new Set(domains)];
  if (uniqueDomains.length !== domains.length) {
    throw new AppError(
      "Question chunks must be requested once per domain.",
      ErrorClassification.INVARIANT_VIOLATION,
      "DUPLICATE_CHUNK_REQUEST",
    );
  }

  return traceAsync(traceId, "question_chunks.load", async () => {
    const modules = await Promise.all(
      uniqueDomains.map(async (domain) => {
        const module = await QUESTION_CHUNK_LOADERS[domain]();
        return assertQuestionChunk(module, domain);
      }),
    );

    const questions = modules.flat();
    const questionIds = new Set(questions.map((question) => question.id));
    if (questionIds.size !== questions.length) {
      throw new AppError(
        "Question identifiers must be globally unique.",
        ErrorClassification.INVARIANT_VIOLATION,
        "DUPLICATE_QUESTION_BANK_ID",
      );
    }

    return questions;
  });
}

export function assertQuestionChunk(
  module: QuestionModule,
  expectedDomain: DomainType,
): readonly Question[] {
  if (!Array.isArray(module.questions) || module.questions.length === 0) {
    throw new AppError(
      "Question chunk must contain a non-empty question array.",
      ErrorClassification.INVARIANT_VIOLATION,
      "INVALID_QUESTION_CHUNK",
    );
  }

  if (!module.questions.every((question) => isQuestion(question, expectedDomain))) {
    throw new AppError(
      "Question chunk failed typed boundary validation.",
      ErrorClassification.INVARIANT_VIOLATION,
      "INVALID_QUESTION_SCHEMA",
    );
  }

  return module.questions;
}

function isQuestion(value: unknown, expectedDomain: DomainType): value is Question {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const question = value as Record<string, unknown>;
  const choices = question.choices;
  const source = question.source;

  return (
    isNonEmptyString(question.id) &&
    Object.values(Track).includes(question.track as Track) &&
    question.domain === expectedDomain &&
    Object.values(Difficulty).includes(question.difficulty as Difficulty) &&
    isNonEmptyString(question.objective) &&
    isNonEmptyString(question.stem) &&
    Array.isArray(choices) &&
    choices.length === DISPLAY_CHOICE_IDS.length &&
    choices.every(isChoice) &&
    new Set(choices.map((choice) => (choice as { id: string }).id)).size ===
      choices.length &&
    isNonEmptyString(question.correctChoiceId) &&
    choices.some(
      (choice) =>
        (choice as { id: string }).id === question.correctChoiceId,
    ) &&
    isNonEmptyString(question.explanation) &&
    isSource(source)
  );
}

function isChoice(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    isNonEmptyString((value as Record<string, unknown>).id) &&
    isNonEmptyString((value as Record<string, unknown>).label)
  );
}

function isSource(value: unknown): boolean {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const source = value as Record<string, unknown>;
  if (!isNonEmptyString(source.title) || !isNonEmptyString(source.url)) {
    return false;
  }

  try {
    const url = new URL(source.url);
    return url.protocol === "https:" && url.hostname.endsWith("cloudflare.com");
  } catch (error: unknown) {
    if (error instanceof TypeError) {
      return false;
    }
    throw error;
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}
